import { useMemo, useState } from "react";
import { Users } from "lucide-react";
import { useAdminOrgs, useAdminUsers } from "../../hooks/queries";
import { agoText, exactText, staleness } from "./userActivity";
import { useOrgStore } from "../../stores/orgStore";
import type { AdminUserRow } from "../../types/contract";
import { Panel } from "./SystemDashboardPanels";

/*
 *
 * One activity timestamp: relative for scanning, absolute on hover for checking.
 *
 * Absent renders "—" and NOT a date. These columns exist to find users who have stopped doing something
 * so "never" and "a long time ago" are the two answers that matter most — and a fabricated date would erase
 * exactly the distinction the panel is for. A pending server field looks the same as a genuine never, which
 * is why the request for these says so explicitly rather than leaving it to be inferred.
 */
function ActivityCell({ iso }: { iso?: string | null }) {
  const text = agoText(iso);
  const stale = staleness(iso);
  return (
    <td
      className={`py-1 pr-3 align-top whitespace-nowrap ${
        stale === "never"
          ? "text-faint"
          : stale === "old"
            ? "text-amber-500"
            : "text-muted"
      }`}
      title={exactText(iso)}
    >
      {text ?? "—"}
    </td>
  );
}

function UserActivityTable({ rows }: { rows: AdminUserRow[] }) {
  return (
    // A TABLE, not a list: the four activity columns only mean anything read DOWN comparing
    // users against each other — "who has stopped using this" is a column-scan question.
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="border-b border-line text-left text-[9px] uppercase tracking-wide text-faint">
            <th className="py-1 pr-3 font-medium">User</th>
            <th
              className="py-1 pr-3 font-medium"
              title="Last authenticated request from this user's client — the 'are they still using it' column"
            >
              Last contact
            </th>
            <th
              className="py-1 pr-3 font-medium"
              title="Most recent field this user added, including fields brought in through a bulk drop"
            >
              Last field
            </th>
            {/*
 Dispatch, not completion — BE-Light's call and the better signal: the two answers
                    diverge exactly for users whose maps keep failing, who are the ones this panel most
                    needs to surface. Someone who asked for ten maps that all failed is active with a
                    problem, not dormant. Saying so in the header beats an admin inferring "completed".
*/}
            <th
              className="py-1 pr-3 font-medium"
              title="When a soil map was last REQUESTED by this user (not when it finished)"
            >
              Last soil map<span className="ml-0.5 text-faint">*</span>
            </th>
            <th
              className="py-1 font-medium"
              title="Last fertility Rx this user saved"
            >
              Last Rx
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line/60">
          {rows.map((u: AdminUserRow) => (
            <tr key={u.user_id} className="hover:bg-hover">
              <td className="py-1 pr-3 align-top">
                <span className="font-medium text-fg">{u.email}</span>
                {u.is_global_admin && (
                  <span className="ml-1 rounded bg-accent/15 px-1 text-[9px] text-accent">
                    global admin
                  </span>
                )}
                <span className="block text-[10px] text-faint">
                  {(u.orgs ?? []).map((o) => o.name ?? o.org_id).join(", ") ||
                    "no organizations"}
                </span>
              </td>
              <ActivityCell iso={u.last_contact_at} />
              <ActivityCell iso={u.last_field_added_at} />
              <ActivityCell iso={u.last_soil_map_at} />
              <ActivityCell iso={u.last_rx_at} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function UsersPanel({ enabled }: { enabled: boolean }) {
  const [orgId, setOrgId] = useState<string | null>(null);
  const [q, setQ] = useState("");
  // The filter must span EVERY org, including ones this admin doesn't belong to -- `orgStore` holds only
  // their own memberships, so it can't serve a system view. Falls back to memberships if the admin orgs
  // read is unavailable, which is better than an empty dropdown.
  const adminOrgs = useAdminOrgs(enabled);
  const ownOrgs = useOrgStore((s) => s.orgs);
  const adminOrgRows = Array.isArray(adminOrgs.data) ? adminOrgs.data : [];
  const orgs = adminOrgRows.length
    ? adminOrgRows.map((o) => ({ org_id: o.org_id, name: o.name ?? o.org_id }))
    : ownOrgs.map((o) => ({ org_id: o.org_id, name: o.name }));
  const users = useAdminUsers(enabled, orgId);

  // The org filter is served (org_id param) so it works across ALL orgs, not just the admin's own; the
  // text box is a local narrowing on top of whatever came back.
  const shown = useMemo(() => {
    const rows = Array.isArray(users.data?.users) ? users.data.users : [];
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((u) => u.email?.toLowerCase().includes(needle));
  }, [users.data, q]);

  return (
    <Panel
      title="Users"
      icon={<Users size={12} />}
      state={{ isLoading: users.isLoading, isError: users.isError }}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <select
          className="rounded border border-line bg-canvas px-1.5 py-1 text-[11px] text-fg"
          value={orgId ?? ""}
          onChange={(e) => setOrgId(e.target.value || null)}
          aria-label="Filter users by organization"
        >
          <option value="">All organizations</option>
          {orgs.map((o) => (
            <option key={o.org_id} value={o.org_id}>
              {o.name}
            </option>
          ))}
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search email…"
          className="min-w-0 flex-1 rounded border border-line bg-canvas px-1.5 py-1 text-[11px] outline-none placeholder:text-faint"
        />
        <span className="text-[10px] text-faint">{shown.length} shown</span>
      </div>
      {/* Without saying so, a wall of em dashes reads as "nobody uses this product", which is the opposite of the truth and exactly the wrong conclusion for a panel built to find dormant users. */}
      <p className="mb-1 text-[9px] text-faint">
        Activity tracking began 28 Jul 2026 — earlier use isn’t recorded, so “—”
        means “nothing since then”.{" "}
        <span className="text-faint">
          * soil map = when the run was requested, not when it finished.
        </span>
      </p>
      {shown.length === 0 ? (
        <p className="text-[11px] text-faint">No users match.</p>
      ) : (
        <UserActivityTable rows={shown} />
      )}
    </Panel>
  );
}
